import { useEffect, useState } from "react";
import { Plus, X, Printer, ArrowsClockwise, Trash } from "@phosphor-icons/react";
import { formatRupiahDot } from "../../lib/format";
import { fetchDokter } from "../../lib/api";
import SearchableSelect from "../SearchableSelect";

const inputCls =
  "w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0A3F9A]";

const Field = ({ label, required, testid, ...props }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[11px] font-bold uppercase tracking-wide text-slate-600">
      {label} {required && <span className="text-red-600">*</span>}
    </label>
    <input type="text" className={inputCls} data-testid={testid} {...props} />
  </div>
);

const sumLastNominal = (t) => {
  const idx = (t.kolom_nominal || []).slice().sort((a, b) => a - b).pop();
  if (idx === undefined) return 0;
  return t.baris.reduce((s, r) => s + (Number(r[idx]) || 0), 0);
};

const colWidth = (name, isNominal) => {
  if (isNominal) return "min-w-[170px] w-[170px]";
  const n = (name || "").toLowerCase();
  if (n === "no" || n === "no." || n === "#") return "min-w-[64px] w-[64px]";
  if (n.startsWith("qty") || n.startsWith("jml") || n.startsWith("jumlah hari") || n === "hari") return "min-w-[90px] w-[90px]";
  return "min-w-[320px]";
};

export default function CustomTableDialog({ table: initial, saving, onCancel, onSubmit }) {
  const [t, setT] = useState(() => ({
    ...initial,
    kolom: [...initial.kolom],
    kolom_nominal: [...(initial.kolom_nominal || [])],
    baris: initial.baris.map((r) => [...r]),
    keterangan: [...(initial.keterangan || [])],
  }));
  const [pasien, setPasien] = useState({
    nama_pasien: "",
    ruang_kelas: "",
    kelas: "",
    umur: "",
    no_mr: "",
    dpjp: "",
  });
  const [dokterList, setDokterList] = useState([]);
  useEffect(() => {
    fetchDokter().then(setDokterList).catch(() => {});
  }, []);

  const setP = (k) => (e) => setPasien({ ...pasien, [k]: e.target.value });
  const nominal = new Set(t.kolom_nominal);
  const valid = pasien.nama_pasien.trim() && pasien.ruang_kelas.trim() && t.judul.trim() && t.baris.length > 0;

  const setCell = (r, c, v) => {
    const baris = t.baris.map((row, i) => (i === r ? row.map((x, j) => (j === c ? v : x)) : row));
    setT({ ...t, baris });
  };
  const setKolom = (c, v) => setT({ ...t, kolom: t.kolom.map((k, i) => (i === c ? v : k)) });
  const toggleNominal = (c) => {
    const next = nominal.has(c) ? t.kolom_nominal.filter((i) => i !== c) : [...t.kolom_nominal, c];
    setT({ ...t, kolom_nominal: next.sort((a, b) => a - b) });
  };
  const addRow = () => setT({ ...t, baris: [...t.baris, t.kolom.map((_, i) => (nominal.has(i) ? 0 : ""))] });
  const removeRow = (r) => setT({ ...t, baris: t.baris.filter((_, i) => i !== r) });
  const addCol = () =>
    setT({ ...t, kolom: [...t.kolom, "Kolom Baru"], baris: t.baris.map((r) => [...r, ""]) });
  const removeCol = (c) =>
    setT({
      ...t,
      kolom: t.kolom.filter((_, i) => i !== c),
      kolom_nominal: t.kolom_nominal.filter((i) => i !== c).map((i) => (i > c ? i - 1 : i)),
      baris: t.baris.map((r) => r.filter((_, i) => i !== c)),
    });
  const recalc = () => setT({ ...t, grand_total: sumLastNominal(t) });

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-3 md:p-6" data-testid="custom-table-dialog">
      <div className="bg-white rounded shadow-xl w-full max-w-7xl h-[94vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between shrink-0">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">Tabel Custom → Kop Surat</div>
            <div className="font-semibold text-slate-900">Periksa &amp; sunting tabel, lengkapi data pasien</div>
          </div>
          <button onClick={onCancel} className="text-slate-500 hover:text-slate-900 p-1" data-testid="custom-dialog-close">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-6 pb-56 flex flex-col gap-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 shrink-0">
            <div className="md:col-span-2">
              <Field label="Judul Dokumen" required value={t.judul} onChange={(e) => setT({ ...t, judul: e.target.value.toUpperCase() })} testid="custom-judul" />
            </div>
            <Field label="Label Total" value={t.total_label} onChange={(e) => setT({ ...t, total_label: e.target.value })} testid="custom-total-label" />
          </div>

          <div className="shrink-0">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-600">
                Rincian Tabel <span className="font-normal normal-case text-slate-500">({t.baris.length} baris · {t.kolom.length} kolom)</span>
              </div>
              <button onClick={addCol} className="inline-flex items-center gap-1 text-xs font-bold text-slate-700 border border-slate-300 px-3 py-1.5 rounded hover:bg-slate-50" data-testid="custom-col-add">
                <Plus size={12} weight="bold" /> Tambah Kolom
              </button>
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded">
              <table className="border-collapse text-sm" style={{ minWidth: "100%" }} data-testid="custom-edit-table">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="border-b border-r border-slate-200 p-2 w-12 text-xs text-slate-500 font-semibold">#</th>
                    {t.kolom.map((k, c) => (
                      <th key={c} className={`border-b border-r border-slate-200 p-2 align-top text-left ${colWidth(k, nominal.has(c))}`}>
                        <input value={k} onChange={(e) => setKolom(c, e.target.value)} className={`${inputCls} font-bold`} data-testid={`custom-col-${c}`} />
                        <div className="flex items-center justify-between mt-1.5 text-[11px] text-slate-500 font-normal">
                          <label className="flex items-center gap-1.5 cursor-pointer select-none">
                            <input type="checkbox" checked={nominal.has(c)} onChange={() => toggleNominal(c)} data-testid={`custom-col-nominal-${c}`} />
                            Rupiah
                          </label>
                          {t.kolom.length > 1 && (
                            <button onClick={() => removeCol(c)} className="text-slate-400 hover:text-red-600 inline-flex items-center gap-0.5" title="Hapus kolom" data-testid={`custom-col-remove-${c}`}>
                              <X size={12} /> hapus
                            </button>
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="border-b border-slate-200 p-2 w-14"></th>
                  </tr>
                </thead>
                <tbody>
                  {t.baris.map((row, r) => (
                    <tr key={r} className="hover:bg-slate-50/60" data-testid={`custom-edit-row-${r}`}>
                      <td className="border-b border-r border-slate-100 p-2 text-center text-xs text-slate-500 tabular-nums">{r + 1}</td>
                      {row.map((v, c) => (
                        <td key={c} className="border-b border-r border-slate-100 p-1.5">
                          {nominal.has(c) ? (
                            <input
                              type="number"
                              value={v ?? 0}
                              onChange={(e) => setCell(r, c, Number(e.target.value) || 0)}
                              className={`${inputCls} text-right tabular-nums`}
                              data-testid={`custom-cell-${r}-${c}`}
                            />
                          ) : (
                            <input value={v ?? ""} onChange={(e) => setCell(r, c, e.target.value)} className={inputCls} data-testid={`custom-cell-${r}-${c}`} />
                          )}
                        </td>
                      ))}
                      <td className="border-b border-slate-100 p-1.5 text-center">
                        <button onClick={() => removeRow(r)} className="text-slate-400 hover:text-red-600 hover:bg-red-50 rounded p-1.5" title="Hapus baris" data-testid={`custom-row-remove-${r}`}>
                          <Trash size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {t.baris.length === 0 && (
                    <tr>
                      <td colSpan={t.kolom.length + 2} className="p-6 text-center text-sm text-slate-500">
                        Belum ada baris. Klik "Tambah Baris".
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
              <button onClick={addRow} className="inline-flex items-center gap-1 text-xs font-bold text-[#0A3F9A] border border-[#0A3F9A] px-3 py-2 rounded hover:bg-blue-50" data-testid="custom-row-add">
                <Plus size={12} weight="bold" /> Tambah Baris
              </button>
              <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded px-4 py-2.5">
                <span className="text-xs font-bold text-slate-700">{t.total_label || "TOTAL"}</span>
                <input
                  type="number"
                  value={t.grand_total ?? 0}
                  onChange={(e) => setT({ ...t, grand_total: Number(e.target.value) || 0 })}
                  className={`${inputCls} w-48 text-right tabular-nums font-bold`}
                  data-testid="custom-grand-total"
                />
                <span className="text-sm text-slate-700 tabular-nums font-semibold whitespace-nowrap">{formatRupiahDot(t.grand_total || 0)}</span>
                <button onClick={recalc} className="text-xs text-[#0A3F9A] font-bold inline-flex items-center gap-1 whitespace-nowrap hover:underline" title="Jumlahkan kolom rupiah terakhir" data-testid="custom-recalc">
                  <ArrowsClockwise size={14} /> Hitung ulang
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5 shrink-0">
            <label className="text-[11px] font-bold uppercase tracking-wide text-slate-600">
              Keterangan (satu poin per baris, dicetak di bagian KETERANGAN)
            </label>
            <textarea
              rows={4}
              value={t.keterangan.join("\n")}
              onChange={(e) => setT({ ...t, keterangan: e.target.value.split("\n") })}
              className={inputCls}
              data-testid="custom-keterangan"
            />
          </div>

          <div className="border-t border-slate-200 pt-5 shrink-0">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">Data Pasien</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Nama Pasien" required value={pasien.nama_pasien} onChange={setP("nama_pasien")} testid="custom-nama" />
              <Field label="Ruang" required value={pasien.ruang_kelas} onChange={setP("ruang_kelas")} placeholder="Mawar 302" testid="custom-ruang" />
              <Field label="Kelas" value={pasien.kelas} onChange={setP("kelas")} placeholder="KELAS 2" testid="custom-kelas" />
              <Field label="Umur" value={pasien.umur} onChange={setP("umur")} testid="custom-umur" />
              <Field label="No MR / Registrasi" value={pasien.no_mr} onChange={setP("no_mr")} testid="custom-nomr" />
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wide text-slate-600">Nama Dr DPJP</label>
                <SearchableSelect
                  value={pasien.dpjp}
                  onChange={(v) => setPasien({ ...pasien, dpjp: v })}
                  options={dokterList.map((d) => ({ value: d.nama, label: d.ksm ? `${d.nama} - ${d.ksm}` : d.nama }))}
                  placeholder="Cari nama dokter..."
                  testid="custom-dpjp"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2 shrink-0">
          <button onClick={onCancel} className="px-4 py-2 text-sm rounded border border-slate-300 hover:bg-slate-50" data-testid="custom-cancel">
            Batal
          </button>
          <button
            onClick={() => onSubmit({ custom_table: { ...t, keterangan: t.keterangan.filter((k) => k.trim()) }, ...pasien })}
            disabled={!valid || saving}
            className="bg-[#057A55] text-white px-5 py-2 rounded hover:bg-[#046148] text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="custom-submit"
          >
            <Printer size={16} weight="duotone" /> {saving ? "Menyimpan..." : "Simpan & Cetak"}
          </button>
        </div>
      </div>
    </div>
  );
}
